"use client";

import { useState, useEffect, useRef, useLayoutEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Notification01Icon, Search01Icon, Globe02Icon, Loading03Icon, Cancel01Icon, Logout01Icon,
  UserIcon, Clock01Icon, CheckmarkCircle02Icon, AlertCircleIcon,
} from "@hugeicons/core-free-icons";
import { get, put, isPlatformAdmin, logout as serviceLogout } from "../../utils/service";
import Drawer from "../ui/Drawer";
import { MobileSidebarTrigger } from "./AppSidebar";
import { getNavTitle } from "./nav-config";
import { useHeaderSlots } from "./header-slots";
import Flag, { currencyName } from "@/components/ui/flag";
import UserAvatar from "@/components/ui/user-avatar";
import SearchInput from "@/components/ui/search-input";

interface Notification { id: string; title: string; message: string; type: string; is_read: boolean; created_at: string }
interface SearchResult {
  id: string; company_name?: string; contact_person?: string; type?: string; status?: string;
  first_name?: string; last_name?: string; result_type: string;
}
interface Currency { currency: string; country_code: string; flag_url: string }
interface ApiOk<T> { success: boolean; data: T }
interface HeaderUser { name: string; email: string; role: string; initials: string; avatar?: string | null }

const notifIcon = (type: string) => {
  if (type.includes("completed") || type.includes("approved")) return <HugeiconsIcon icon={CheckmarkCircle02Icon} size={16} className="text-green-600" />;
  if (type.includes("pending") || type.includes("updated")) return <HugeiconsIcon icon={Clock01Icon} size={16} className="text-orange-600" />;
  if (type.includes("overdue") || type.includes("delayed")) return <HugeiconsIcon icon={AlertCircleIcon} size={16} className="text-red-600" />;
  return <HugeiconsIcon icon={Notification01Icon} size={16} className="text-primary" />;
};

export default function Header({ user, onOpenMobileSidebar }: { user: HeaderUser; onOpenMobileSidebar: () => void }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { setTitleSlot, setActionsSlot, registered } = useHeaderSlots();
  const headerRef = useRef<HTMLElement>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  // Search covers organization records, which system administrators never see.
  const platformAdmin = isPlatformAdmin();
  const searchRef = useRef<HTMLDivElement>(null);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [notifOpen, setNotifOpen] = useState(false);
  const [currencyOpen, setCurrencyOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [currencyPref, setCurrencyPref] = useState<Currency | null>(null);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [currencyQuery, setCurrencyQuery] = useState("");
  const [pendingCurrency, setPendingCurrency] = useState("");
  const [updatingCurrency, setUpdatingCurrency] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);

  const unread = notifications.filter((n) => !n.is_read).length;

  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const set = () => document.documentElement.style.setProperty("--header-h", `${el.offsetHeight}px`);
    set();
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    get<ApiOk<Currency>>("/exchange/preference").then((r) => r.success && setCurrencyPref(r.data)).catch(() => {});
    get<ApiOk<Currency[]>>("/exchange/currencies/flags").then((r) => r.success && setCurrencies(r.data)).catch(() => {});
    get<ApiOk<Notification[]>>("/notifications?page=1&page_size=10").then((r) => r.success && setNotifications(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (platformAdmin) return;
    const open = () => setSearchOpen(true);
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setSearchOpen(true); }
    };
    window.addEventListener("open-search", open);
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("open-search", open); window.removeEventListener("keydown", onKey); };
  }, [platformAdmin]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (!t.closest(".user-menu-container")) setUserMenuOpen(false);
      if (searchRef.current && !searchRef.current.contains(t)) { setShowResults(false); setSearchOpen(false); }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const runSearch = async (q: string) => {
    setSearching(true);
    try {
      if (q.trim().length < 2) { setResults([]); return; }
      const r = await get<ApiOk<{ results: Record<string, Array<{ id: string; title: string; subtitle?: string }>> }>>(`/search?q=${encodeURIComponent(q)}&limit=4`);
      // Flatten the grouped results (accounts, leads, deals ...) into one list.
      setResults(r.success ? Object.entries(r.data.results).flatMap(([type, rows]) => rows.map((x) => ({ id: `${type}:${x.id}`, company_name: x.title, contact_person: x.subtitle, result_type: type }))) : []);
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const onSearchChange = (value: string) => {
    setQuery(value);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (!value.trim()) { setResults([]); setShowResults(false); return; }
    setShowResults(true);
    setSearching(true);
    searchTimeout.current = setTimeout(() => runSearch(value), 300);
  };

  const openResult = () => {
    navigate(`/search?q=${encodeURIComponent(query)}`);
    setShowResults(false);
    setQuery("");
  };

  const confirmCurrency = async () => {
    setUpdatingCurrency(true);
    try {
      const r = await put<ApiOk<Currency>>("/exchange/preference", { currency: pendingCurrency });
      if (r.success) { setCurrencyPref(r.data); window.location.reload(); }
    } catch (e) {
      console.error("Error updating currency preference:", e);
    } finally {
      setUpdatingCurrency(false);
    }
  };

  const filteredCurrencies = currencies.filter(
    (c) =>
      c.currency.toLowerCase().includes(currencyQuery.toLowerCase()) ||
      currencyName(c.currency).toLowerCase().includes(currencyQuery.toLowerCase()) ||
      c.country_code.toLowerCase().includes(currencyQuery.toLowerCase())
  );

  return (
    <header
      ref={headerRef}
      className="relative z-30 shrink-0"
    >
      <div className="min-h-16 px-4 sm:px-6 py-2 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <MobileSidebarTrigger onClick={onOpenMobileSidebar} />
          <div ref={setTitleSlot} className="min-w-0" />
          {registered === 0 && <h1 className="text-base font-semibold text-gray-900 truncate">{getNavTitle(location.pathname)}</h1>}
        </div>

        <div className="flex-1" />

        <div ref={actionsSlot_ref => setActionsSlot(actionsSlot_ref)} className="flex items-center gap-2 shrink-0" />

        <div className="flex items-center gap-1 shrink-0">
          <div ref={searchRef} className={platformAdmin ? "hidden" : "relative"}>
            <button
              type="button"
              onClick={() => setSearchOpen((v) => !v)}
              className="p-2 rounded-full text-gray-500 hover:text-gray-900 hover:bg-primary/10 transition-colors"
              title="Search"
            >
              <HugeiconsIcon icon={Search01Icon} size={17} />
            </button>
            {searchOpen && (
              <div className="absolute top-full right-0 mt-2 w-[min(22rem,calc(100vw-2rem))] rounded-xl bg-white shadow-2xl border border-primary/10 z-50">
                <div className="flex items-center h-10 m-2 rounded-lg bg-primary/10 px-3 gap-2">
                  {searching ? <HugeiconsIcon icon={Loading03Icon} size={16} className="text-gray-500 animate-spin" /> : <HugeiconsIcon icon={Search01Icon} size={16} className="text-gray-500" />}
                  <input
                    autoFocus
                    value={query}
                    onChange={(e) => onSearchChange(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && query.trim() && (openResult(), setSearchOpen(false))}
                    placeholder="Search..."
                    className="flex-1 bg-transparent outline-none text-xs text-gray-900 placeholder:text-gray-500"
                  />
                  {query && (
                    <button onClick={() => { setQuery(""); setResults([]); setShowResults(false); }} className="text-gray-500 hover:text-gray-900">
                      <HugeiconsIcon icon={Cancel01Icon} size={14} />
                    </button>
                  )}
                </div>
                {showResults && (
                  <div className="max-h-72 overflow-y-auto border-t border-primary/10">
                    {searching ? (
                      <div className="p-4 text-center text-xs text-gray-500">Searching...</div>
                    ) : results.length ? (
                      results.map((r) => (
                        <div key={r.id} onClick={() => { openResult(); setSearchOpen(false); }} className="p-3 hover:bg-primary/10 cursor-pointer">
                          <p className="text-xs font-semibold text-gray-900">{r.company_name}</p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            <span className="capitalize">{r.result_type.replace(/s$/, "")}</span>{r.contact_person ? ` · ${r.contact_person}` : ""}
                          </p>
                        </div>
                      ))
                    ) : (
                      <div className="p-4 text-center text-xs text-gray-500">No results found for "{query}"</div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => setCurrencyOpen(true)}
            className="flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-gray-700 hover:border-primary/40 hover:text-gray-900 transition-colors"
            title={currencyPref ? `Display currency: ${currencyName(currencyPref.currency)}` : "Change currency"}
          >
            {currencyPref ? (
              <><Flag code={currencyPref.country_code} className="h-3 w-[18px]" />{currencyPref.currency}</>
            ) : (
              <HugeiconsIcon icon={Globe02Icon} size={17} />
            )}
          </button>
          <button
            type="button"
            onClick={() => setNotifOpen(true)}
            className="relative p-2 rounded-full text-gray-500 hover:text-gray-900 hover:bg-primary/10 transition-colors"
            title="Notifications"
          >
            <HugeiconsIcon icon={Notification01Icon} size={17} />
            {unread > 0 && (
              <span className="absolute top-0.5 right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] flex items-center justify-center rounded-full font-semibold">
                {unread}
              </span>
            )}
          </button>

          <div className="relative user-menu-container ml-1">
            <button
              type="button"
              onClick={() => setUserMenuOpen((v) => !v)}
              className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              title={user.name}
            >
              <UserAvatar src={user.avatar} name={user.name} className="h-9 w-9" />
            </button>
            <AnimatePresence>
              {userMenuOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -8, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.95 }}
                  className="absolute top-full right-0 mt-2 w-64 bg-white border border-primary/10 rounded-xl shadow-2xl z-50"
                >
                  <div className="p-4 border-b border-primary/10">
                    <p className="text-xs font-semibold text-gray-900 truncate">{user.name}</p>
                    <p className="text-xs text-gray-500 truncate">{user.email}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{user.role}</p>
                  </div>
                  <div className="py-1">
                    <button
                      onClick={() => { navigate("/settings"); setUserMenuOpen(false); }}
                      className="flex w-full items-center px-4 py-2.5 text-xs text-gray-700 hover:bg-primary/10"
                    >
                      <HugeiconsIcon icon={UserIcon} size={16} className="mr-3" /> Profile Settings
                    </button>
                    <button
                      onClick={() => serviceLogout()}
                      className="flex w-full items-center px-4 py-2.5 text-xs text-red-500 hover:bg-primary/10 border-t border-primary/10"
                    >
                      <HugeiconsIcon icon={Logout01Icon} size={16} className="mr-3" /> Sign out
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      <Drawer theme="light" isOpen={notifOpen} onClose={() => setNotifOpen(false)} position="right" title="Notifications">
        <div className="p-3">
          {notifications.length ? (
            notifications.map((n) => (
              <div key={n.id} className="p-3 rounded-lg hover:bg-primary/10 border-b border-primary/10 last:border-0">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">{notifIcon(n.type)}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-gray-900 mb-1">{n.title}</p>
                    <p className="text-xs text-gray-500 mb-2">{n.message}</p>
                    <p className="text-xs text-gray-400">{new Date(n.created_at).toLocaleDateString()}</p>
                    {!n.is_read && <span className="inline-block mt-2 text-xs font-semibold px-2 py-0.5 rounded bg-primary text-white">New</span>}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="p-6 text-center text-gray-500 text-xs">No notifications</div>
          )}
          <button
            onClick={() => { navigate("/notifications"); setNotifOpen(false); }}
            className="w-full mt-2 text-xs font-semibold text-primary hover:bg-primary/10 py-2 rounded-lg"
          >
            View All Notifications
          </button>
        </div>
      </Drawer>

      <Drawer theme="light" isOpen={currencyOpen} onClose={() => { setCurrencyOpen(false); setPendingCurrency(""); }} position="right" title="Select Currency">
        <div className="p-3">
          {pendingCurrency ? (
            <div className="space-y-4">
              <p className="text-xs text-gray-600">
                This will update all monetary values across the application to display in {pendingCurrency}.
              </p>
              <div className="bg-primary/10 rounded-lg p-4 flex items-center justify-between text-xs">
                <span className="font-semibold text-gray-900">{currencyPref?.currency || "USD"}</span>
                <span>→</span>
                <span className="font-semibold text-primary">{pendingCurrency}</span>
              </div>
              <div className="flex gap-3">
                <button onClick={() => setPendingCurrency("")} disabled={updatingCurrency} className="flex-1 px-4 py-2.5 text-xs font-semibold text-gray-700 bg-primary/10 rounded-lg disabled:opacity-50">
                  Cancel
                </button>
                <button onClick={confirmCurrency} disabled={updatingCurrency} className="flex-1 px-4 py-2.5 text-xs font-semibold text-white bg-primary rounded-lg disabled:opacity-50 flex items-center justify-center gap-2">
                  {updatingCurrency ? <><HugeiconsIcon icon={Loading03Icon} size={16} className="animate-spin" /> Updating...</> : "Confirm Change"}
                </button>
              </div>
            </div>
          ) : (
            <>
              <SearchInput className="mb-3" placeholder="Search currencies…" value={currencyQuery} onChange={(e) => setCurrencyQuery(e.target.value)} />
              {filteredCurrencies.length ? (
                filteredCurrencies.map((c) => (
                  <button
                    key={c.currency}
                    onClick={() => setPendingCurrency(c.currency)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-primary/10 ${
                      currencyPref?.currency === c.currency ? "bg-primary/10 text-primary" : "text-gray-700"
                    }`}
                  >
                    <Flag code={c.country_code} className="h-4 w-6 shrink-0" />
                    <div className="flex-1 min-w-0 text-left">
                      <p className="text-xs font-semibold">{c.currency}</p>
                      <p className="truncate text-xs text-gray-500">{currencyName(c.currency)}</p>
                    </div>
                    {currencyPref?.currency === c.currency && <div className="w-2 h-2 bg-primary rounded-full" />}
                  </button>
                ))
              ) : (
                <div className="p-4 text-center text-gray-500 text-xs">No currencies found</div>
              )}
            </>
          )}
        </div>
      </Drawer>
    </header>
  );
}
